// pipe (info tips): the ERP-side InfoTip — the shared plain-language "what is this?" affordance,
// gated by the global help-tips preference (modules/erp/erp-info-tips.ts). With the tips OFF a
// custom trigger (e.g. the Restricted marker) still renders bare; the default ⓘ icon renders
// not at all.
//
// Import discipline (pure-view-safe): @affine/component + @affine/i18n + the module-level store —
// no DI/services/workbench, so every pure *-view.tsx seam can mount it under happy-dom.
import { InfoTip } from '@affine/component';
import { erpInfoTips$ } from '@affine/core/modules/erp/erp-info-tips';
import { useI18n } from '@affine/i18n';
import { useLiveData } from '@toeverything/infra';
import { cloneElement, type ReactElement, type ReactNode } from 'react';

export interface ErpInfoTipProps {
  content?: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Optional custom trigger (must take a ref); renders bare when the tips are off. */
  children?: ReactElement;
  /** Accessible name override for the default ⓘ trigger — distinguishes clustered tips. */
  label?: string;
  /** Custom triggers become a tab stop while a tip is attached (default). Labels repeated
   *  per tile/row opt out so a dashboard never grows dozens of tab stops. */
  focusable?: boolean;
  /** Extra class for the default ⓘ trigger (spacing at call sites). */
  className?: string;
  'data-testid'?: string;
}

export function ErpInfoTip({
  content,
  side,
  align,
  children,
  label,
  focusable = true,
  className,
  'data-testid': testId,
}: ErpInfoTipProps) {
  const t = useI18n();
  const enabled = useLiveData(erpInfoTips$);
  if (!enabled) {
    // Tips off: the custom trigger renders bare — and without the injected tabIndex, so a
    // disabled tip never leaves a dead tab stop behind.
    return children ?? null;
  }
  const trigger =
    children && focusable
      ? cloneElement(children as ReactElement<{ tabIndex?: number }>, {
          tabIndex:
            (children.props as { tabIndex?: number }).tabIndex ?? 0,
        })
      : children;
  return (
    <InfoTip
      content={content}
      label={label ?? t['com.affine.pipe.tooltip.whats-this']()}
      side={side}
      align={align}
      className={className}
      data-testid={testId}
    >
      {trigger}
    </InfoTip>
  );
}
